import { forwardRef, useImperativeHandle, useRef, useState, useMemo, useEffect } from 'react';
import { Flex, Icon, Text, Input, Modal, Collapse, useKeyboardSelectableList } from '@nula/core-ui';
import clsx from 'clsx';
import type { SlashCommand } from './slash-commands';
import { SLASH_COMMANDS } from './slash-commands';
import styles from './slash-menu.module.css';

// ── Shared sub-components ─────────────────────────────────────────────────────

function CategoryLabel({ label }: { label: string }) {
  return (
    <Text size="xs" type="secondary" className={styles.categoryLabel}>
      {label}
    </Text>
  );
}

function CategoryDivider() {
  return <div className={styles.categoryDivider} />;
}

// ── SlashMenu ─────────────────────────────────────────────────────────────────

export interface SlashMenuProps {
  items: SlashCommand[];
  onSelect: (cmd: SlashCommand) => void;
  /** True when the user has typed at least one character after / */
  isQuerying?: boolean;
}

export interface SlashMenuHandle {
  onKeyDown: (event: KeyboardEvent) => void;
}

export const SlashMenu = forwardRef<SlashMenuHandle, SlashMenuProps>(
  function SlashMenu({ items, onSelect, isQuerying = false }, ref) {
    const { highlightedIndex, getItemProps, getListProps } = useKeyboardSelectableList({
      items,
      onSelect,
      loop: true,
    });

    const listProps = getListProps();
    const keyDownRef = useRef(listProps.onKeyDown);
    keyDownRef.current = listProps.onKeyDown;

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: (event: KeyboardEvent) => {
          keyDownRef.current(event as unknown as React.KeyboardEvent);
        },
      }),
      [],
    );

    // Build rows: category labels + dividers in no-query (grouped) mode
    const rows = useMemo(() => {
      if (isQuerying || items.length === 0) return null;
      const result: React.ReactNode[] = [];
      let lastCategory = '';
      items.forEach((cmd, index) => {
        if (cmd.category && cmd.category !== lastCategory) {
          if (lastCategory !== '') result.push(<CategoryDivider key={`div-${cmd.category}`} />);
          result.push(<CategoryLabel key={`cat-${cmd.category}`} label={cmd.category} />);
        }
        lastCategory = cmd.category ?? lastCategory;
        const itemProps = getItemProps(index);
        result.push(
          <Flex
            key={cmd.label}
            align="center"
            gap="sm"
            className={clsx(styles.item, index === highlightedIndex && styles.itemHighlighted)}
            role={itemProps.role}
            aria-selected={itemProps['aria-selected']}
            onMouseEnter={itemProps.onMouseEnter}
            onClick={itemProps.onClick}
          >
            <Text type="secondary" className={styles.iconWrap}>
              <Icon name={cmd.icon} size={14} weight={300} />
            </Text>
            <Text size="sm" ellipsis>{cmd.label}</Text>
          </Flex>,
        );
      });
      return result;
    }, [isQuerying, items, highlightedIndex, getItemProps]);

    return (
      <Flex vertical className={styles.menu} {...listProps}>
        {/* Hint row — no-query state only */}
        {!isQuerying && (
          <>
            <Flex align="center" gap="sm" className={clsx(styles.item, styles.hintRow)}>
              <Text type="secondary" className={styles.iconWrap}>
                <Icon name="help" size={14} weight={300} />
              </Text>
              <Text size="sm" type="secondary">
                <Text size="sm" code>/?</Text>{' — all commands'}
              </Text>
            </Flex>
            {items.length > 0 && <CategoryDivider key="hint-div" />}
          </>
        )}

        {/* Grouped items (no-query) or flat items (querying) */}
        {isQuerying
          ? items.map((cmd, index) => {
              const itemProps = getItemProps(index);
              return (
                <Flex
                  key={cmd.label}
                  align="center"
                  gap="sm"
                  className={clsx(styles.item, index === highlightedIndex && styles.itemHighlighted)}
                  role={itemProps.role}
                  aria-selected={itemProps['aria-selected']}
                  onMouseEnter={itemProps.onMouseEnter}
                  onClick={itemProps.onClick}
                >
                  <Text type="secondary" className={styles.iconWrap}>
                    <Icon name={cmd.icon} size={14} weight={300} />
                  </Text>
                  <Text size="sm" ellipsis>{cmd.label}</Text>
                </Flex>
              );
            })
          : rows}

        {/* Empty state — only shown when actively searching and nothing matches */}
        {isQuerying && items.length === 0 && (
          <Flex className={styles.emptyState}>
            <Icon name="search_off" size={50} weight={300} className={styles.emptyStateIcon} />
            <Text size="sm" type="secondary">No results</Text>
          </Flex>
        )}
      </Flex>
    );
  },
);

// ── CommandReferenceModal ─────────────────────────────────────────────────────

export interface CommandReferenceModalProps {
  open: boolean;
  onClose: () => void;
  commands?: SlashCommand[];
}

function scoreCommand(cmd: SlashCommand, query: string): number {
  const q = query.toLowerCase();
  const label = cmd.label.toLowerCase();
  const category = cmd.category.toLowerCase();
  if (label === q) return 100;
  if (label.startsWith(q)) return 90;
  if (label.split(/[\s:]+/).some((w) => w.startsWith(q))) return 80;
  if (label.includes(q)) return 70;
  if (category.startsWith(q)) return 60;
  if (category.includes(q)) return 50;
  if (cmd.description.toLowerCase().includes(q)) return 40;
  let li = 0;
  for (let qi = 0; qi < q.length; qi++) {
    li = label.indexOf(q[qi], li);
    if (li === -1) return 0;
    li++;
  }
  return 20;
}

export function CommandReferenceModal({ open, onClose, commands = SLASH_COMMANDS }: CommandReferenceModalProps) {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return commands;
    return commands
      .map((c) => ({ c, score: scoreCommand(c, search.trim()) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ c }) => c);
  }, [commands, search]);

  const categories = useMemo(() => {
    const map = new Map<string, SlashCommand[]>();
    filtered.forEach((cmd) => {
      if (!map.has(cmd.category)) map.set(cmd.category, []);
      map.get(cmd.category)!.push(cmd);
    });
    return map;
  }, [filtered]);

  const collapseItems = useMemo(() =>
    Array.from(categories.entries()).map(([category, items]) => ({
      key: category,
      header: category,
      count: items.length,
      children: (
        <Flex vertical className={styles.referenceCategoryItems}>
          {items.map((cmd) => (
            <Flex key={cmd.label} align="center" gap="sm" className={styles.referenceItem}>
              <Flex align="center" justify="center" className={styles.referenceItemIcon}>
                <Icon name={cmd.icon} size={14} weight={300} />
              </Flex>
              <Flex vertical className={styles.referenceItemText}>
                <Text size="sm">{cmd.label}</Text>
                <Text size="xs" type="secondary">{cmd.description}</Text>
              </Flex>
            </Flex>
          ))}
        </Flex>
      ),
    })),
    [categories]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<span className={styles.referenceModalTitle}>Command reference</span>}
      footer={null}
      width={600}
      centered
    >
      <Flex vertical gap="sm" className={styles.referenceModal}>
        <Input
          size="sm"
          placeholder="Search commands…"
          prefix={<Icon name="search" size={14} weight={300} />}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        />
        {collapseItems.length === 0 ? (
          <Flex className={styles.emptyState}>
            <Icon name="search_off" size={50} weight={300} className={styles.emptyStateIcon} />
            <Text size="sm" type="secondary">No commands found</Text>
          </Flex>
        ) : (
          <Collapse
            key={search.trim() ? 'searching' : 'idle'}
            items={collapseItems}
            defaultActiveKey={search.trim() ? Array.from(categories.keys()) : []}
            ghost
          />
        )}
      </Flex>
    </Modal>
  );
}
